import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ArrowLeft, GripVertical } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import SingleImageField from "../../components/common/SingleImageField";

const blank = {
  label: "",
  key: "",
  input_type: "number",
  unit_type: "both",
  is_required: true,
  min_value: "",
  max_value: "",
  help_text: "",
  example_image_url: "",
  sort_order: 0,
  is_active: true,
};

const keyify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function AdminMeasurementFields() {
  const { ctypeId } = useParams();
  const [ctype, setCtype] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [t, f] = await Promise.all([
        api.get("/admin/stitching/clothing-types"),
        api.get(`/admin/stitching/clothing-types/${ctypeId}/fields`),
      ]);
      setCtype((t.data || []).find((x) => x.id === ctypeId));
      setRows(f.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [ctypeId]);

  const openNew = () => {
    setEditingId(null);
    setForm(blank);
    setDialogOpen(true);
  };
  const openEdit = (f) => {
    setEditingId(f.id);
    setForm({
      ...blank,
      ...f,
      min_value: f.min_value ?? "",
      max_value: f.max_value ?? "",
      help_text: f.help_text || "",
      example_image_url: f.example_image_url || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.label.trim()) return toast.error("Label required");
    const body = {
      label: form.label.trim(),
      key: form.key ? keyify(form.key) : keyify(form.label),
      input_type: form.input_type,
      unit_type: form.unit_type,
      is_required: form.is_required,
      min_value: form.min_value === "" ? null : Number(form.min_value),
      max_value: form.max_value === "" ? null : Number(form.max_value),
      options: [],
      help_text: form.help_text.trim() || null,
      example_image_url: form.example_image_url.trim() || null,
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active,
    };
    try {
      if (editingId) await api.put(`/admin/stitching/fields/${editingId}`, body);
      else await api.post(`/admin/stitching/clothing-types/${ctypeId}/fields`, body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this field?")) return;
    try {
      await api.delete(`/admin/stitching/fields/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  // ---- Drag-to-reorder ----
  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);

  const commitReorder = async (next) => {
    setRows(next);
    const items = next.map((r, idx) => ({ id: r.id, sort_order: idx + 1 }));
    try {
      await api.post(`/admin/stitching/clothing-types/${ctypeId}/fields/reorder`, { items });
      toast.success("Order saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      load();
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragOver = (id) => (e) => { e.preventDefault(); if (id !== dropId) setDropId(id); };
  const onDragEnd = () => { setDragId(null); setDropId(null); };
  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { onDragEnd(); return; }
    const src = rows.findIndex((r) => r.id === dragId);
    const tgt = rows.findIndex((r) => r.id === targetId);
    if (src < 0 || tgt < 0) { onDragEnd(); return; }
    const next = [...rows];
    const [moved] = next.splice(src, 1);
    next.splice(tgt, 0, moved);
    onDragEnd();
    await commitReorder(next);
  };

  return (
    <div data-testid="admin-measurement-fields-page">
      <Link to="/admin/stitching" className="text-xs inline-flex items-center gap-1 mb-3 text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]">
        <ArrowLeft size={12} /> Back to Clothing Types
      </Link>
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Fields for</div>
          <h1 className="font-display text-4xl">{ctype?.name || "…"}</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Define the measurement fields customers fill for this clothing type.</p>
        </div>
        <button className="kb-btn-primary inline-flex items-center gap-2" onClick={openNew} data-testid="field-add-btn">
          <Plus size={14} /> New Field
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="fields-loading">Loading…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="fields-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">No fields yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3">#</th>
                <th>Label</th>
                <th>Key</th>
                <th>Type</th>
                <th>Unit</th>
                <th>Range</th>
                <th>Req</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  draggable
                  onDragStart={onDragStart(r.id)}
                  onDragOver={onDragOver(r.id)}
                  onDrop={onDrop(r.id)}
                  onDragEnd={onDragEnd}
                  className={`border-t border-[hsl(var(--kb-border))] transition-colors ${
                    dropId === r.id && dragId !== r.id ? "bg-[hsl(var(--kb-surface-alt))]" : ""
                  } ${dragId === r.id ? "opacity-50" : ""}`}
                  data-testid={`field-row-${r.key}`}
                >
                  <td className="p-2 text-[hsl(var(--kb-muted))]">
                    <span className="cursor-grab active:cursor-grabbing" title="Drag to reorder" data-testid={`field-drag-${r.key}`}>
                      <GripVertical size={14} />
                    </span>
                  </td>
                  <td className="p-3 font-mono">{r.sort_order}</td>
                  <td className="p-3">{r.label}</td>
                  <td className="p-3 font-mono text-xs">{r.key}</td>
                  <td className="p-3 text-xs uppercase">{r.input_type}</td>
                  <td className="p-3 text-xs uppercase">{r.unit_type}</td>
                  <td className="p-3 text-xs font-mono">{r.min_value ?? "–"} – {r.max_value ?? "–"}</td>
                  <td className="p-3 text-xs">{r.is_required ? "yes" : "no"}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(r)} className="p-2 hover:bg-[hsl(var(--kb-surface-alt))]" data-testid={`field-edit-${r.key}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(r.id)} className="p-2 hover:bg-[hsl(var(--kb-surface-alt))] text-red-700" data-testid={`field-del-${r.key}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Measurement field"
        subtitle={editingId ? "Edit" : "New"}
        maxWidth="max-w-xl"
        testid="field-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)}>Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="field-save-btn">{editingId ? "Update" : "Create"}</button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className="kb-input" placeholder="Label (e.g. Shoulder)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value, key: form.key || keyify(e.target.value) })} data-testid="field-label" />
            <input className="kb-input font-mono" placeholder="Key (e.g. shoulder)" value={form.key} onChange={(e) => setForm({ ...form, key: keyify(e.target.value) })} data-testid="field-key" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <select className="kb-input" value={form.input_type} onChange={(e) => setForm({ ...form, input_type: e.target.value })}>
              <option value="number">Number</option>
              <option value="text">Text</option>
              <option value="dropdown">Dropdown</option>
            </select>
            <select className="kb-input" value={form.unit_type} onChange={(e) => setForm({ ...form, unit_type: e.target.value })}>
              <option value="inches">Inches</option>
              <option value="cm">Centimeters</option>
              <option value="both">Both</option>
            </select>
            <input type="number" className="kb-input" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" step="0.1" className="kb-input" placeholder="Min value" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} data-testid="field-min" />
            <input type="number" step="0.1" className="kb-input" placeholder="Max value" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value })} data-testid="field-max" />
          </div>
          <textarea rows={2} className="kb-input" placeholder="Help text" maxLength={240} value={form.help_text} onChange={(e) => setForm({ ...form, help_text: e.target.value })} />
          <SingleImageField
            label="Example image (optional)"
            value={form.example_image_url}
            onChange={(key) => setForm({ ...form, example_image_url: key || "" })}
            hint="Shown to customers while taking this measurement"
            aspect="aspect-[4/3]"
            testid="field-example-image"
          />
          <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_required} onChange={(e) => setForm({ ...form, is_required: e.target.checked })} /> Required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
            </label>
          </div>
        </div>
      </AdminDialog>
    </div>
  );
}
